import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../services/supabase";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { FaEye, FaPencilAlt, FaTimesCircle } from "react-icons/fa";


const PAGE_SIZE = 15;

export default function RidesPage() {
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("requested_at");
  const [sortDir, setSortDir] = useState("desc");

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRide, setSelectedRide] = useState(null);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const navigate = useNavigate();

  /* ===============================
     LOAD RIDES (pagination + search)
     =============================== */
  const loadRides = useCallback(async () => {
  setLoading(true);

  const from = (page - 1) * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from("rides")
    .select(
      `
      id,
      client_id,
      driver_id,
      pickup_address,
      dropoff_address,
      requested_at,
      started_at,
      date_heure,
      meta,
      status,
      clients:profiles!client_id(full_name, phone, email),
      drivers:drivers(id, profiles(full_name))
    `,
      { count: "exact" }
    )
    .order(sortBy, { ascending: sortDir === "asc" })
    .range(from, to);

  if (search) {
  query = query.or(
    `pickup_address.ilike.%${search}%,dropoff_address.ilike.%${search}%,client_name_cached.ilike.%${search}%,client_phone_cached.ilike.%${search}%,client_email_cached.ilike.%${search}%,started_at_text.ilike.%${search}%`
  );
}

// 🎯 Filtre par plage de dates
if (dateFrom) {
  query = query.gte(
    "requested_at",
    `${dateFrom}T00:00:00`
  );
}

if (dateTo) {
  query = query.lte(
    "requested_at",
    `${dateTo}T23:59:59`
  );
}


  const { data, error, count } = await query;

  if (error) {
    console.error("Erreur chargement rides:", error);
  } else {
    setRides(data || []);
    setTotalCount(count || 0);
  }

  setLoading(false);
}, [page, search, sortBy, sortDir, dateFrom, dateTo]);


const fetchAllRidesForExport = async () => {
  let query = supabase
    .from("rides")
    .select(
      `
      id,
      client_id,
      driver_id,
      pickup_address,
      dropoff_address,
      requested_at,
      date_heure,
      meta,
      status,
      clients:profiles!client_id(full_name, phone, email),
      drivers:drivers(id, profiles(full_name))
    `
    )
    .order(sortBy, { ascending: sortDir === "asc" });

  if (search) {
    query = query.or(
      `pickup_address.ilike.%${search}%,dropoff_address.ilike.%${search}%,client_name_cached.ilike.%${search}%,client_phone_cached.ilike.%${search}%,client_email_cached.ilike.%${search}%,meta_telephone.ilike.%${search}%`
    );
  }

  if (dateFrom) {
    query = query.gte("requested_at", `${dateFrom}T00:00:00`);
  }

  if (dateTo) {
    query = query.lte("requested_at", `${dateTo}T23:59:59`);
  }

  const { data, error } = await query;

  if (error) {
    alert("Erreur lors du chargement pour export");
    console.error(error);
    return [];
  }

  return data || [];
};



  /* ===============================
     LOAD DRIVERS
     =============================== */
  const loadDrivers = async () => {
    const { data, error } = await supabase
      .from("drivers")
      .select(`
        id,
        profiles(full_name)
      `)
      .eq("approved", true)
      .eq("is_active", true);

    if (!error) setDrivers(data || []);
  };

/*
  useEffect(() => {
  loadRides();
}, [loadRides]);
*/

// Chargement initial + pagination + tri
useEffect(() => {
  if (search) return; // ⛔ bloque si recherche active
  loadRides();
}, [page, sortBy, sortDir, search, dateFrom, dateTo, loadRides]);

// Recherche (debounce)
useEffect(() => {
  if (!search) return;

  const t = setTimeout(() => {
    loadRides();
  }, 300);

  return () => clearTimeout(t);
}, [search, loadRides]);


  useEffect(() => {
    loadDrivers();
  }, []);

  /* ===============================
     UPDATE DRIVER ASSIGNMENT
     =============================== */
  const updateRideDriver = async (rideId, driverId) => {
  const payload = {
    driver_id: driverId || null,
    status: driverId ? "accepted" : "unassigned",
  };

  const { error } = await supabase
    .from("rides")
    .update(payload)
    .eq("id", rideId);

  if (error) {
    alert("Erreur lors de l’assignation");
    console.error(error);
    return;
  }

  loadRides();
};

// Annuler une course
const cancelRide = async (rideId) => {
  const confirm = window.confirm(
    "Êtes-vous sûr de vouloir annuler cette course ?"
  );

  if (!confirm) return;

  const { error } = await supabase
    .from("rides")
    .update({
      status: "canceled",
      canceled_at: new Date().toISOString(),
      driver_id: null,
    })
    .eq("id", rideId)
    .not("status", "in", "(completed,canceled)");

  if (error) {
    alert("Erreur lors de l’annulation de la course");
    console.error(error);
    return;
  }

  loadRides();
};


  /* ===============================
     EXPORT EXCEL
     =============================== */
  const exportToExcel = async () => {
  let rows = [];

  if (selectedIds.length > 0) {
    // export sélection manuelle
    rows = rides.filter((r) => selectedIds.includes(r.id));
  } else if (dateFrom || dateTo || search) {
    // export global filtré (hors pagination)
    rows = await fetchAllRidesForExport();
  } else {
    // fallback : export page courante
    rows = rides;
  }

  if (rows.length === 0) {
    alert("Aucune course à exporter");
    return;
  }

  const data = rows.map((r) => ({
    ID: r.id,
    Client: r.clients?.full_name,
    Téléphone: r.clients?.phone,
    Email: r.clients?.email,
    Chauffeur: r.drivers?.profiles?.full_name || "—",
    Pickup: r.pickup_address,
    Destination: r.dropoff_address,
    Statut: r.status,
    Date: r.date_heure || r.requested_at,
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Courses");

  XLSX.writeFile(wb, `courses_${dateFrom || "all"}_${dateTo || "all"}.xlsx`);
};


  /* ===============================
     STATUS RENDER
     =============================== */
  const renderStatus = (status) => {
    const map = {
      unassigned: "status-red",
      requested: "status-orange",
      accepted: "status-blue",
      started: "status-green",
      completed: "status-darkgreen",
      canceled: "status-gray",
    };
    return <span className={map[status] || ""}>{status}</span>;
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  /* ===============================
     RENDER
     =============================== */
  return (
    <div>
        <div className="content-header">

        <h2>Courses</h2>

        <button className="btn-primary" onClick={() => navigate("/rides/create")}>
            Créer une course
        </button>
        </div>

      {/* TOOLBAR */}
      <div className="rides-toolbar">
        <input
          type="text"
          placeholder="Recherche client / chauffeur / email / téléphone"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />

        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="requested_at">Date</option>
          <option value="status">Statut</option>
        </select>

        <select value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
          <option value="desc">↓ Desc</option>
          <option value="asc">↑ Asc</option>
        </select>

        <button className="btn-secondary" onClick={exportToExcel}>
          Export Excel
        </button> 
      </div>

      {(dateFrom || dateTo) && (
          <small style={{ opacity: 0.7 }}>
            Export de toutes les courses du{" "}
            {dateFrom || "début"} au {dateTo || "aujourd’hui"}
          </small>
        )}
      
      <div className="rides-toolbar">
        <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setPage(1);
              setDateFrom(e.target.value);
            }}
            title="Date de début"
          />

          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setPage(1);
              setDateTo(e.target.value);
            }}
            title="Date de fin"
          />

          <button
            className="btn-secondary"
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              setPage(1);
            }}
          >
            Réinitialiser dates
          </button>
      </div>

      {/* TABLE */}
      {loading ? (
        <p>Chargement…</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={(e) =>
                    setSelectedIds(
                      e.target.checked ? rides.map((r) => r.id) : []
                    )
                  }
                />
              </th>
              <th>ID</th>
              <th>Client</th>
              <th>Chauffeur</th>
              <th>Pickup</th>
              <th>Destination</th>
              <th>Date</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {rides.map((ride) => (
              <tr key={ride.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(ride.id)}
                    onChange={(e) =>
                      setSelectedIds(
                        e.target.checked
                          ? [...selectedIds, ride.id]
                          : selectedIds.filter((id) => id !== ride.id)
                      )
                    }
                  />
                </td>

                <td>{ride.id.slice(0, 8)}…</td>
                <td>{ride.clients?.full_name || "—"}</td>

                <td>
                  <select
                    value={ride.driver_id || ""}
                    disabled={ride.status === "completed"}
                    onChange={(e) =>
                      updateRideDriver(ride.id, e.target.value)
                    }
                    title={
                      ride.status === "completed"
                        ? "Course complétée — modification interdite"
                        : "Assigner un chauffeur"
                    }
                  >
                    <option value="">— Non assigné —</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.profiles?.full_name}
                      </option>
                    ))}
                  </select>
                </td>

                <td>{ride.pickup_address}</td>
                <td>{ride.dropoff_address}</td>

                <td>
                  {ride.date_heure
                    ? new Date(ride.date_heure).toLocaleString()
                    : new Date(ride.requested_at).toLocaleString()}
                </td>

                <td>{renderStatus(ride.status)}</td>

                <td>
                  <div className="icone-course">
                  <button
                    className="btn-secondary"
                    onClick={() => setSelectedRide(ride)}
                    title="Voir"
                  >
                    <FaEye />
                  </button>

                  <button
                    className="btn-secondary"
                    disabled={ride.status === "completed"}
                    onClick={() => navigate(`/rides/${ride.id}/edit`)}
                    title={
                    ride.status === "completed"
                      ? "Course complétée — modification impossible"
                      : "Modifier"
                  }
                  >
                    <FaPencilAlt />
                  </button>

                  {/* ANNULER */}
                        <button
                          className="btn-danger"
                          disabled={["completed", "canceled", "started"].includes(ride.status)}
                          onClick={() => cancelRide(ride.id)}
                          title={
                            ["completed", "canceled", "started"].includes(ride.status)
                              ? "Annulation impossible"
                              : "Annuler la course"
                          }
                        >
                          <FaTimesCircle />
                        </button>

                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* PAGINATION */}
      <div className="pagination">
        <button disabled={page === 1} onClick={() => setPage(page - 1)}>
          ← Précédent
        </button>
        <span>
          Page {page} / {totalPages || 1}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Suivant →
        </button>
      </div>

      {/* MODAL */}
      {selectedRide && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Détails de la course</h3>
            
            {selectedRide.meta?.type_reservation === "planifie" && <p style={{ color: "blue" }}><em>Course planifiée</em></p>}

            <p><strong>ID :</strong> {selectedRide.id}</p>
            <p><strong>Client :</strong> {selectedRide.clients?.full_name}</p>
            <p><strong>Téléphone :</strong> {selectedRide.meta?.telephone || "—"}</p>
            <p><strong>Email :</strong> {selectedRide.meta?.email || "non fournie"}</p>
            <p><strong>Chauffeur :</strong> {selectedRide.drivers?.profiles?.full_name || "Aucun chauffeur assigné"}</p>
            <p><strong>Pickup :</strong> {selectedRide.pickup_address}</p>
            <p><strong>Destination :</strong> {selectedRide.dropoff_address}</p>
            <p><strong>Date et Heure :</strong> {selectedRide.date_heure ? new Date(selectedRide.date_heure).toLocaleString() : "Course Immédiate"}</p>
            <p><strong>Statut :</strong> {selectedRide.status}</p>
            <p style={{ fontSize: 10, color: "gray" }}><strong>Date de création de la course :</strong> {new Date(selectedRide.requested_at).toLocaleString()}</p>

            <div style={{ textAlign: "right" }}>
              <button className="btn-danger" onClick={() => setSelectedRide(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

